/**
 * `/productivity/formulas` — Task U17 (Tier-3 live-tenant readiness), plan unit U17,
 * KTD4, KTD13 and R18.
 *
 * The arithmetic a person's effectiveness is computed with has existed since WP-J1:
 * six endpoints, a closed vocabulary of nine components, an immutability trigger and
 * a ladder. None of it could be reached from a browser. A company that wanted to say
 * "attendance is worth less here than task results" needed a database client.
 *
 * ── The lifecycle is not this screen's ──────────────────────────────────────
 *
 * `useVersionedConfig` (U14, KTD13) owns list → create → patch → activate, the
 * touched-fields draft and the Skeleton / Empty / Retry states, and
 * `VersionedConfigPanel` owns what each of them looks like. What is here is this
 * configuration's own words, its own arithmetic, and the two things a formula has that
 * no other configuration on this hook does: **weights that must add up**, and
 * **versions that freeze the moment a score references them**.
 *
 * ══════════════════════════════════════════════════════════════════════════════
 *  THIS IS THE SURFACE WITH NO CONCURRENCY TOKEN, AND IT SAYS SO ON SCREEN
 * ══════════════════════════════════════════════════════════════════════════════
 *
 * `PATCH /productivity/formulas/{id}` and `POST /{id}/activate` read no `If-Match`,
 * take no body version and set no `ETag`. So `preconditionOf` answers
 * `{ kind: 'none' }` for every row — the kind U14's fix round 1 added for exactly this
 * backend — and the request goes out with no token and no header. `null` would have
 * been the wrong answer twice: it means *the token is missing*, and the hook refuses
 * every write made on such a row, which on a backend that has no token at all is every
 * write this screen exists to make. Conflating the two is the defect `{ kind: 'none' }`
 * was added to fix.
 *
 * What follows from that is a fact about the product, not an implementation detail, so
 * it is on the screen and not only in this comment: **two administrators editing the
 * same formula will not be stopped, and the later save wins.** The hook's whole
 * conflict apparatus — the note, the stored-value compare, the overwrite
 * acknowledgement — is keyed on `WORKFORCE_VERSION_CONFLICT`, which this backend never
 * sends, so none of it can fire here. Saying "last write wins" out loud is the only
 * honest substitute, and it is what `VersionedConfigPrecondition`'s own docstring asks
 * a `{ kind: 'none' }` screen to do.
 *
 * ── Activation takes no body, which is why the button works here ────────────
 *
 * The plan told U16–U18 to check what activation takes before trusting the button,
 * after U15 shipped an Activate that governed nothing: U14's `activate` sends
 * `bodyWith({}, precondition)`, and `/policies`' activation takes a `make_default`
 * flag it therefore could not carry. Here `POST /{id}/activate` reads **no body at
 * all** — the handler takes the id from the path and calls
 * `activateFormulaVersion(db, actor, id)` — so the empty body is precisely the request
 * the contract documents, and `state.activate` is used unmodified. Of the four
 * consumers this is the only one for which that is true.
 *
 * The second activation is `activate: true` on the **create** body, which creates the
 * version and switches the rung over in one transaction. It is create-only, so it is
 * screen state rather than a draft key: `ProductivityFormulaPatchRequest` is
 * `additionalProperties: false`, and a draft holding it would put it into the next
 * `PATCH`. `based_on_formula_id` is create-only for the same reason.
 *
 * ── Editing the active version means creating a new one ─────────────────────
 *
 * That is the backend's rule and not a choice made here. `PATCH` refuses anything but
 * a `draft` (`409 WORKFORCE_INVALID_STATE_TRANSITION`) and refuses a version any score
 * has referenced whatever its status (`409 WORKFORCE_FORMULA_IMMUTABLE`, enforced by
 * the trigger `wp_formulas_immutable` under every statement, not by the service). So
 * the control on an in-use or in-force row is **"create a new version from this one"**,
 * which posts with `based_on_formula_id` and inherits its `formula_key`, and the edit
 * control is **absent** on those rows rather than disabled — the codebase's
 * control-availability rule. It is absent because `isEditable(version)` is false, not
 * because the row has no precondition: the hook's only per-row gate is
 * `preconditionOf`, and routing a policy refusal through the channel that means
 * "token missing" is the conflation this file refuses to make. See the U17 report.
 *
 * ── One hook, two lists, because the hook models versions of ONE thing ──────
 *
 * `useVersionedConfig` requires `isActive` to pick exactly one row and compares the
 * draft against it. A company's formulas are a **ladder**: `wp_formula_active_scope_uk`
 * permits one `active` row per rung, so a catalogue can hold four active formulas —
 * one for a job title, one for a department, one for a role, one for the company — and
 * every one of them is genuinely in force. Rather than lie about that, the screen moves
 * the hook's own list, exactly as U16 does:
 *
 *   catalogue        `listPath = /productivity/formulas`   every key, every version.
 *   one lineage      `?formula_key=K`                      one key, in which exactly
 *                                                          one row is active, so the
 *                                                          diff's baseline is the
 *                                                          version really in force.
 *
 * ── R18 ─────────────────────────────────────────────────────────────────────
 *
 * This screen configures how a person's effectiveness is computed, which makes it the
 * second screen in the product to have to say what a score is not. It says it in the
 * register `AlertRulesScreen` and `EffectivenessScreen` already use: explainable,
 * carried with a confidence, and never a quota or a ranking. The nine components are a
 * closed vocabulary with no free-text key, so no weight typed here can name a target.
 */

import { useEffect, useState, type ReactNode } from 'react';

import { useI18n } from '../../i18n';
import type { MessageKey } from '../../i18n/messages';
import {
  DEFAULT_FORMULA_COMPONENTS,
  DEFAULT_FORMULA_KEY,
  DEFAULT_NORMALIZE,
  FORMULAS_PATH,
  FORMULA_COMPONENT_DIRECTION,
  FORMULA_COMPONENT_KEYS,
  activateFormulaPath,
  componentInputsOf,
  componentSetProblems,
  droppedComponents,
  formatHundredths,
  formulaKeyPath,
  formulaPath,
  isActivatable,
  isEditable,
  positiveWeightHundredths,
  REQUIRED_POSITIVE_TOTAL,
  type ComponentSetProblem,
  type FormulaComponentInput,
  type FormulaComponentKey,
  type ProductivityFormula,
  type ProductivityFormulaCreateRequest,
  type ProductivityFormulaDraft,
  type ProductivityFormulaListResponse,
  type WeightProblem,
} from '../../api/formulas';
import { VersionedConfigPanel } from '../../config/VersionedConfigPanel';
import { useVersionedConfig } from '../../config/useVersionedConfig';
import { ScreenHeading } from '../../shell/ScreenHeading';

/** Which list the hook is reading. */
type Mode = { readonly kind: 'catalogue' } | { readonly kind: 'lineage'; readonly key: string };

/**
 * What the composer is doing, and it is not one thing.
 *
 * `create` posts a new version — from a base version, or from Master §13.2's default
 * when the company has none. `patch` edits a draft in place, which the backend permits
 * only while nothing has scored against it.
 */
interface Composer {
  readonly intent: 'create' | 'patch';
  /** The version this composition starts from. `null` only for a company's first. */
  readonly baseId: string | null;
}

/** The Vietnamese name of each component — the same nine `COMPONENT_LABEL` names. */
const COMPONENT_LABEL: Readonly<Record<FormulaComponentKey, MessageKey>> = {
  task_result: 'formulas.component.task_result',
  on_time: 'formulas.component.on_time',
  quality: 'formulas.component.quality',
  productive_time: 'formulas.component.productive_time',
  focus: 'formulas.component.focus',
  attendance: 'formulas.component.attendance',
  workload_balance: 'formulas.component.workload_balance',
  verified_risk_penalty: 'formulas.component.verified_risk_penalty',
  approved_manual_adjustment: 'formulas.component.approved_manual_adjustment',
};

/**
 * **The explanation each component carries**, and every one of them is transcribed from
 * the measurement that computes it (`src/modules/scoring/score.ts`), not written to
 * sound reassuring.
 *
 * This is the half of Master §13.3 an administrator needs: a weight is a decision about
 * somebody, and the person taking it should be able to read what the number is a
 * fraction of and which table it comes from before they raise it. `quality` is the one
 * that matters most — nothing in this build measures it, so a formula that names it
 * lowers every score's confidence and moves no score at all — and it says so here
 * rather than in a warning nobody sees until a month of scores has been computed.
 */
const COMPONENT_HELP: Readonly<Record<FormulaComponentKey, MessageKey>> = {
  task_result: 'formulas.component.help.task_result',
  on_time: 'formulas.component.help.on_time',
  quality: 'formulas.component.help.quality',
  productive_time: 'formulas.component.help.productive_time',
  focus: 'formulas.component.help.focus',
  attendance: 'formulas.component.help.attendance',
  workload_balance: 'formulas.component.help.workload_balance',
  verified_risk_penalty: 'formulas.component.help.verified_risk_penalty',
  approved_manual_adjustment: 'formulas.component.help.approved_manual_adjustment',
};

const PROBLEM_MESSAGE: Readonly<Record<ComponentSetProblem['kind'], MessageKey>> = {
  empty: 'formulas.weights.empty',
  weight: 'formulas.weights.badWeight',
  zero_positive: 'formulas.weights.zeroPositive',
  sum: 'formulas.weights.sum',
};

/**
 * Why one weight is inadmissible, in the language the rest of the screen speaks.
 *
 * `WeightProblem` is the client's copy of the server's own enum, and it is a wire
 * value: printing `too_many_decimals` into a Vietnamese sentence puts an English
 * identifier in front of somebody two lines under a checkbox that says "Chất lượng".
 * The same applies to the component key itself, which is why every refusal below
 * interpolates `COMPONENT_LABEL` rather than the raw key.
 */
const WEIGHT_PROBLEM_MESSAGE: Readonly<Record<WeightProblem, MessageKey>> = {
  not_finite: 'formulas.weights.problem.not_finite',
  negative: 'formulas.weights.problem.negative',
  above_100: 'formulas.weights.problem.above_100',
  too_many_decimals: 'formulas.weights.problem.too_many_decimals',
};

/** One component in the shape a request carries, always built the same way round. */
function componentOf(key: FormulaComponentKey, weight: number): FormulaComponentInput {
  return { key, weight, normalize: DEFAULT_NORMALIZE };
}

export function FormulasScreen(): ReactNode {
  const { t } = useI18n();
  const [mode, setMode] = useState<Mode>({ kind: 'catalogue' });
  const [composer, setComposer] = useState<Composer | null>(null);
  /**
   * `activate: true` on the create body — create-only, so screen state rather than a
   * draft key. Defaults to `false`, which is the contract's default and the safer of
   * the two: a formula that starts judging people the moment somebody presses a button
   * for the first time is not a default this product should choose for an administrator.
   */
  const [activateOnCreate, setActivateOnCreate] = useState(false);
  /**
   * A write was sent **from the composer** and has not landed yet.
   *
   * Fix round 1, and the flag exists rather than a bare `state.saved` because `saved`
   * is true after an *activate* too, and an activate is pressed on a row while the
   * composer may be open on something else. Closing the form on somebody else's
   * landed write would take a half-typed formula off the screen.
   */
  const [submittedFromComposer, setSubmittedFromComposer] = useState(false);

  /**
   * The version the composition starts from, assigned **below** — after the hook has
   * read the list — and closed over by `createBody`.
   *
   * A `let` rather than a `const` above the hook because the two need each other: the
   * surface is built before any version exists, and `createBody` is called at press
   * time, by which point the assignment at the bottom of this render has long run. The
   * alternative — keeping the whole row in `composer` — would freeze a copy that a
   * refetch no longer matches.
   */
  let baseVersion: ProductivityFormula | null = null;

  const state = useVersionedConfig<
    ProductivityFormulaListResponse,
    ProductivityFormula,
    ProductivityFormulaDraft,
    ProductivityFormulaCreateRequest
  >({
    listPath: mode.kind === 'lineage' ? formulaKeyPath(mode.key) : FORMULAS_PATH,
    versions: (list) => list.data,
    idOf: (formula) => formula.id,
    labelOf: (formula) => `${formula.name} · ${formula.formula_key} · v${String(formula.version)}`,
    // True of every `active` row, because that is what is true: one per rung, and the
    // catalogue holds every rung. In `lineage` the list is one key, so the hook's
    // "exactly one" holds and the diff's baseline is the version actually in force.
    isActive: (formula) => formula.status === 'active',
    fieldsOf: (formula) => ({
      // The first three keys are spelled as `ProductivityFormulaDraft` spells them and
      // rendered the way the hook's own `renderValue` renders a draft value — nothing
      // types that correspondence, and a mismatch is a silent "the version in force has
      // no such field" in the diff rather than an error.
      //
      // `components` is therefore `JSON.stringify` of the request shape and cannot be
      // anything else: `renderValue` stringifies the draft's array, and the diff joins
      // the two sides by string equality. It is also, in the same breath, the value the
      // panel prints into the settings column, which is why a second human-readable
      // `weights` field used to sit beside it saying the same thing in words. That
      // duplicate is gone: `fieldsOf` conflates "what to compare" with "what to show",
      // and until the hook separates the two the honest move is to render the
      // comparable string once rather than twice. See the report's §4.
      name: formula.name,
      description: formula.description ?? 'null',
      components: JSON.stringify(componentInputsOf(formula)),
      // Rendered and not editable, which `fieldsOf` explicitly allows.
      version: String(formula.version),
      status: formula.status,
      scope:
        formula.scope_key === null
          ? formula.scope_type
          : `${formula.scope_type}:${formula.scope_key}`,
      positive_weight_total: formula.positive_weight_total,
      in_use: String(formula.in_use),
      effective_from: formula.effective_from,
      // A retired version's end date. Rendered for the same reason `effective_from` is:
      // the ladder is read-only on this screen, and read-only is only honest while the
      // whole of it is on the row.
      effective_to: formula.effective_to ?? 'null',
      // Only when there is one, so no row carries an empty field. A component the
      // current vocabulary does not contain cannot be sent back, and it is a hole in
      // the sum of any version branched from this one — see `formulas.dropped`.
      ...(droppedComponents(formula).length === 0
        ? {}
        : {
            dropped_components: droppedComponents(formula)
              .map((component) => component.key)
              .join(', '),
          }),
    }),
    /*
      `{ kind: 'none' }` for every row, and never `null`.

      There is no `If-Match`, no body version and no `ETag` on this surface, so there is
      no token to carry and none to be missing. `null` means the token is missing and
      makes the hook refuse the write; returning it here would have silently swallowed
      every write this screen makes, which is the defect fix round 1 added this kind to
      fix. Whether a particular version may be written is a different question, decided
      by `isEditable`/`isActivatable` at the control rather than smuggled down this
      channel — see the header.
    */
    preconditionOf: () => ({ kind: 'none' }),
    createPath: FORMULAS_PATH,
    createBody: (draft) => {
      const base = baseVersion;
      const components =
        draft.components ?? (base === null ? DEFAULT_FORMULA_COMPONENTS : componentInputsOf(base));
      return {
        name: (draft.name ?? base?.name ?? '').trim(),
        description:
          'description' in draft ? (draft.description ?? null) : (base?.description ?? null),
        components,
        // A base means "the next version of that key": the server inherits its
        // `formula_key`, so sending a key as well would be a second opinion about the
        // same thing. With no base there is no key to inherit and the slug of a
        // Vietnamese name is a row of hyphens, so the key is stated.
        ...(base === null
          ? { formula_key: DEFAULT_FORMULA_KEY }
          : { based_on_formula_id: base.id }),
        // Always sent, never left to the contract's default, so that what the button
        // says and what the request carries cannot drift apart.
        activate: activateOnCreate,
      };
    },
    patchPath: (formula) => formulaPath(formula.id),
    // No body, and that is the contract: the handler reads the id from the path and
    // nothing else. `bodyWith({}, {kind:'none'})` is `{}`, which is what it takes.
    activation: {
      kind: 'endpoint',
      method: 'POST',
      path: (formula) => activateFormulaPath(formula.id),
    },
  });

  baseVersion =
    composer?.baseId == null
      ? null
      : (state.versions.find((formula) => formula.id === composer.baseId) ?? null);

  /** The value on screen: what was typed, else what the base version holds, else the default. */
  const composedComponents: readonly FormulaComponentInput[] =
    state.draft.components ??
    (baseVersion === null ? DEFAULT_FORMULA_COMPONENTS : componentInputsOf(baseVersion));
  const composedName = state.draft.name ?? baseVersion?.name ?? '';
  // `??` would be wrong here and `touched` is why: a description cleared to `null` is a
  // typed value, and falling back through it would put the base version's sentence back
  // on screen and into the request.
  const composedDescription = state.touched.includes('description')
    ? (state.draft.description ?? null)
    : (baseVersion?.description ?? null);

  /**
   * The components of the base version that `componentInputsOf` had to leave behind.
   *
   * The wire publishes `known` for exactly this, and until fix round 1 nothing on the
   * screen read it: a version carrying an unknown **positive** component lost that
   * component's weight silently, the running total could no longer reach 100, and the
   * only explanation offered was `formulas.weights.sum` blaming weights nobody had
   * touched — so a legacy formula was unbranchable through the one screen that exists
   * to succeed it.
   */
  const dropped = baseVersion === null ? [] : droppedComponents(baseVersion);

  const problems = componentSetProblems(composedComponents);
  const positiveTotal = positiveWeightHundredths(composedComponents);
  const nameGiven = composedName.trim() !== '';
  const writeAllowed =
    composer !== null &&
    (composer.intent === 'patch'
      ? baseVersion !== null && state.canWrite(baseVersion)
      : state.canWrite());
  const canSubmit = problems.length === 0 && nameGiven && writeAllowed;

  const closeComposer = (): void => {
    setComposer(null);
    setActivateOnCreate(false);
    setSubmittedFromComposer(false);
    state.resetDraft();
  };

  /*
    ── The composer closes on its own landed write (fix round 1) ──────────────

    Without this the form had no exit at all on success. The hook empties the draft on
    the transition to `ok`, so the composer simply re-rendered with the *base version's*
    values restored under the same heading — a full, valid, submittable formula — with
    `activateOnCreate` still ticked from the first press and `u17-submit` live again. A
    second press therefore created a **duplicate permanent version** and, with the box
    still ticked, activated it, retiring the version just put into force and changing
    what every employee is scored with. `wp_formulas_guard` refuses DELETE and this
    screen offers no retire, so there was no way back from a browser.

    `state.resetDraft()` is deliberately NOT called here. It clears the hook's
    `lastAction`, and with it `saved` — which is the panel's confirmation that the write
    landed. Closing the form must not also delete the only sentence saying why.
  */
  const { saved } = state;
  useEffect(() => {
    if (!submittedFromComposer || !saved) return;
    // The close itself, and the box with it: a composer opened next starts unticked
    // rather than inheriting this write's activation.
    setComposer(null);
    setActivateOnCreate(false);
    setSubmittedFromComposer(false);
  }, [submittedFromComposer, saved]);

  const openComposer = (formula: ProductivityFormula, intent: Composer['intent']): void => {
    // The draft was typed against a version that is about to leave the form; carrying
    // it into another composition would put somebody else's weights into a formula
    // they were not looking at.
    state.resetDraft();
    setActivateOnCreate(false);
    setSubmittedFromComposer(false);
    setMode({ kind: 'lineage', key: formula.formula_key });
    setComposer({ intent, baseId: formula.id });
  };

  const setComponents = (next: readonly FormulaComponentInput[]): void => {
    state.setField('components', next);
  };

  const toggleComponent = (key: FormulaComponentKey, include: boolean): void => {
    if (!include) {
      setComponents(composedComponents.filter((component) => component.key !== key));
      return;
    }
    if (composedComponents.some((component) => component.key === key)) return;
    // Appended in the vocabulary's order, so a set built by clicking around reads the
    // same as one read back from the server.
    const next = [...composedComponents, componentOf(key, 0)];
    setComponents(
      [...next].sort(
        (left, right) =>
          FORMULA_COMPONENT_KEYS.indexOf(left.key) - FORMULA_COMPONENT_KEYS.indexOf(right.key),
      ),
    );
  };

  const setWeight = (key: FormulaComponentKey, weight: number): void => {
    setComponents(
      composedComponents.map((component) =>
        component.key === key ? componentOf(key, weight) : component,
      ),
    );
  };

  const createAction =
    composer === null ? (
      <button
        type="button"
        className="wp-button wp-button--primary"
        data-testid="u17-create-default"
        onClick={() => {
          state.resetDraft();
          setActivateOnCreate(false);
          setMode({ kind: 'catalogue' });
          setComposer({ intent: 'create', baseId: null });
        }}
      >
        {t('formulas.create.default')}
      </button>
    ) : (
      <button type="button" className="wp-button" data-testid="u17-close" onClick={closeComposer}>
        {t('formulas.close')}
      </button>
    );

  return (
    <div className="wp-stack">
      <ScreenHeading>{t('formulas.title')}</ScreenHeading>
      <p className="wp-small wp-muted">{t('formulas.intro')}</p>

      {/* R18, said on screen and not only in the code: this is arithmetic that has to
          explain itself, and it is never a quota. Same register as `alertRules.noQuota`
          and `effectiveness.framing.body`, which say the same thing about the numbers
          this formula produces. */}
      <p className="wp-small wp-muted" data-testid="u17-no-quota">
        {t('formulas.noQuota')}
      </p>

      {/* The `{ kind: 'none' }` precondition, in words. There is no compare-and-set on
          this surface, so the hook's conflict note can never appear and the person
          deserves to be told what the server will not do for them. */}
      <p className="wp-small wp-muted" data-testid="u17-no-cas">
        {t('formulas.noConcurrencyCheck')}
      </p>

      {mode.kind === 'lineage' ? (
        <p className="wp-small">
          <button
            type="button"
            className="wp-button"
            data-testid="u17-back"
            onClick={() => {
              setMode({ kind: 'catalogue' });
              closeComposer();
            }}
          >
            {t('formulas.back')}
          </button>
        </p>
      ) : null}

      <VersionedConfigPanel
        {...state}
        title={
          mode.kind === 'lineage'
            ? t('formulas.panel.lineage', { key: mode.key })
            : t('formulas.panel.title')
        }
        subject={t('formulas.subject')}
        emptyTitle={t('formulas.empty.title')}
        emptyDescription={t('formulas.empty.body')}
        createAction={createAction}
        testId="u17-config"
        rowAction={(row) => {
          const formula = state.versions.find((one) => one.id === row.id);
          if (formula === undefined) return null;
          return (
            <>
              {/* Absent, not disabled, on an in-use or in-force version: the server
                  refuses the PATCH, so a control here would be present and inert. */}
              {isEditable(formula) ? (
                <button
                  type="button"
                  className="wp-button"
                  data-testid={`u17-edit-${row.id}`}
                  onClick={() => {
                    openComposer(formula, 'patch');
                  }}
                >
                  {t('formulas.row.edit')}
                </button>
              ) : null}
              {isActivatable(formula) ? (
                <button
                  type="button"
                  className="wp-button"
                  data-testid={`u17-activate-${row.id}`}
                  onClick={() => {
                    // Not the composer's write, so a landed activation must not close a
                    // form somebody is still typing into.
                    setSubmittedFromComposer(false);
                    state.activate(formula);
                  }}
                >
                  {t('formulas.row.activate')}
                </button>
              ) : null}
              {/* Offered on every row, in-use ones included: creating the next version
                  of a frozen formula is exactly what freezing it leaves you. */}
              <button
                type="button"
                className="wp-button"
                data-testid={`u17-branch-${row.id}`}
                onClick={() => {
                  openComposer(formula, 'create');
                }}
              >
                {t('formulas.row.branch')}
              </button>
            </>
          );
        }}
      />

      <p className="wp-small wp-muted" data-testid="u17-immutable-note">
        {t('formulas.immutable.note')}
      </p>

      {/* `POST /{id}/retire` is mounted and this screen does not call it. Omitting the
          control is a decision (retiring the only company-rung version makes everybody
          `unconfigured`); omitting the sentence is not, on a screen already carrying
          three standing notes about what the server does and does not do. */}
      <p className="wp-small wp-muted" data-testid="u17-retire-note">
        {t('formulas.retire.note')}
      </p>

      {composer === null ? null : (
        <section
          className="wp-section"
          aria-labelledby="u17-composer-heading"
          data-testid="u17-composer"
        >
          <h2 className="wp-section__title" id="u17-composer-heading">
            {composer.intent === 'patch'
              ? t('formulas.composer.patch', { name: baseVersion?.name ?? '' })
              : baseVersion === null
                ? t('formulas.composer.first')
                : t('formulas.composer.from', {
                    name: baseVersion.name,
                    version: String(baseVersion.version),
                  })}
          </h2>

          {dropped.length === 0 ? null : (
            <p className="wp-small" data-testid="u17-dropped">
              {t('formulas.dropped', {
                keys: dropped
                  .map((component) => `${component.key} (${String(component.weight)})`)
                  .join(', '),
              })}
            </p>
          )}

          <div className="wp-field">
            <label className="wp-field__label" htmlFor="u17-name">
              {t('formulas.field.name')}
            </label>
            <input
              className="wp-field__input"
              id="u17-name"
              data-testid="u17-name"
              type="text"
              maxLength={120}
              value={composedName}
              onChange={(event) => {
                state.setField('name', event.target.value);
              }}
            />
          </div>

          <div className="wp-field">
            <label className="wp-field__label" htmlFor="u17-description">
              {t('formulas.field.description')}
            </label>
            <input
              className="wp-field__input"
              id="u17-description"
              data-testid="u17-description"
              type="text"
              maxLength={1000}
              value={composedDescription ?? ''}
              onChange={(event) => {
                state.setField(
                  'description',
                  event.target.value === '' ? null : event.target.value,
                );
              }}
            />
          </div>

          {/* A weight sheet, not a stack of fields: the nine components are read down
              one column and their weights down another, because the whole question the
              editor answers is "do these add up to 100". Same shape as the payslip
              ledger one screen over — a ruled line per component and a totals line
              under them. */}
          <fieldset className="wp-weights" data-testid="u17-components">
            <legend>{t('formulas.components.legend')}</legend>
            <p className="wp-small wp-muted">{t('formulas.components.hint')}</p>
            {FORMULA_COMPONENT_KEYS.map((key) => {
              const included = composedComponents.find((component) => component.key === key);
              return (
                <div className="wp-weights__line" key={key} data-testid={`u17-component-${key}`}>
                  <label className="wp-toggle" htmlFor={`u17-include-${key}`}>
                    <input
                      id={`u17-include-${key}`}
                      type="checkbox"
                      data-testid={`u17-include-${key}`}
                      checked={included !== undefined}
                      onChange={(event) => {
                        toggleComponent(key, event.target.checked);
                      }}
                    />
                    <span>
                      {t(COMPONENT_LABEL[key])}
                      {' · '}
                      {t(
                        FORMULA_COMPONENT_DIRECTION[key] === 'negative'
                          ? 'formulas.direction.negative'
                          : 'formulas.direction.positive',
                      )}
                    </span>
                  </label>
                  {included === undefined ? null : (
                    <div className="wp-weights__amount">
                      <label className="wp-field__label" htmlFor={`u17-weight-${key}`}>
                        {t('formulas.field.weight')}
                      </label>
                      <input
                        className="wp-field__input wp-num"
                        id={`u17-weight-${key}`}
                        data-testid={`u17-weight-${key}`}
                        type="number"
                        min={0}
                        max={100}
                        step={0.01}
                        value={included.weight}
                        onChange={(event) => {
                          setWeight(key, Number(event.target.value));
                        }}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </fieldset>

          {/*
            `positiveWeightHundredths` answers `null` when any positive weight is
            inadmissible — three decimal places, say — because there is then no sum to
            state. Printing `formatHundredths(0)` for that read "Tổng trọng số dương:
            0.00 / 100.00", which is not the total and is not zero, directly beside a
            problem list saying something else entirely. `null` gets its own sentence.
          */}
          <p className="wp-weights__total" data-testid="u17-total">
            {positiveTotal === null
              ? t('formulas.weights.total.unknown', {
                  required: formatHundredths(REQUIRED_POSITIVE_TOTAL * 100),
                })
              : t('formulas.weights.total', {
                  total: formatHundredths(positiveTotal),
                  required: formatHundredths(REQUIRED_POSITIVE_TOTAL * 100),
                })}
          </p>

          {/*
            The inline refusal, before any request. It is not the gate — `validateComponents`
            refuses the same set with `400 WORKFORCE_FORMULA_WEIGHTS_INVALID` and
            `wp_formula_weights_ck` refuses the row underneath it — it is the round trip
            the person does not have to make, computed with the server's own integer
            arithmetic so the two cannot disagree.
          */}
          {problems.length === 0 ? null : (
            <ul className="wp-small" data-testid="u17-weights-error">
              {problems.map((problem) => (
                <li key={`${problem.kind}-${'key' in problem ? problem.key : ''}`}>
                  {t(PROBLEM_MESSAGE[problem.kind], {
                    // The component's Vietnamese name and the problem's Vietnamese
                    // words, not `task_result` and `too_many_decimals`: those are wire
                    // values, and the checkbox two lines above already says "Chất
                    // lượng".
                    key: 'key' in problem ? t(COMPONENT_LABEL[problem.key]) : '',
                    problem:
                      problem.kind === 'weight' ? t(WEIGHT_PROBLEM_MESSAGE[problem.problem]) : '',
                    total: problem.kind === 'sum' ? formatHundredths(problem.total) : '',
                    required: formatHundredths(REQUIRED_POSITIVE_TOTAL * 100),
                  })}
                </li>
              ))}
            </ul>
          )}

          {composer.intent === 'create' ? (
            <div className="wp-field">
              <label className="wp-toggle" htmlFor="u17-activate-now">
                <input
                  id="u17-activate-now"
                  type="checkbox"
                  data-testid="u17-activate-now"
                  checked={activateOnCreate}
                  onChange={(event) => {
                    setActivateOnCreate(event.target.checked);
                  }}
                />
                <span>{t('formulas.activateNow')}</span>
              </label>
              <p className="wp-small wp-muted">{t('formulas.activateNow.hint')}</p>
            </div>
          ) : null}

          <div className="wp-filters__actions">
            {canSubmit ? (
              <button
                type="button"
                className="wp-button wp-button--primary"
                data-testid="u17-submit"
                onClick={() => {
                  if (composer.intent === 'patch') {
                    if (baseVersion === null) return;
                    setSubmittedFromComposer(true);
                    state.patch(baseVersion);
                    return;
                  }
                  setSubmittedFromComposer(true);
                  state.create();
                }}
              >
                {composer.intent === 'patch'
                  ? t('formulas.submit.patch')
                  : t('formulas.submit.create')}
              </button>
            ) : (
              <p className="wp-small wp-muted" data-testid="u17-submit-blocked">
                {problems.length > 0
                  ? t('formulas.submit.blocked.weights')
                  : nameGiven
                    ? t('formulas.submit.blocked.pending')
                    : t('formulas.submit.blocked.name')}
              </p>
            )}
            {state.touched.length === 0 ? null : (
              <button
                type="button"
                className="wp-button"
                data-testid="u17-discard"
                onClick={() => {
                  state.resetDraft();
                }}
              >
                {t('formulas.discard')}
              </button>
            )}
          </div>
        </section>
      )}

      {/*
        What each component means, always on screen and not folded into the composer.

        Master §13.3 gives the person being scored a right to an explanation; this is the
        same right one step earlier, for the person choosing the weights. Every sentence
        is transcribed from the measurement that computes the number — what it is a
        fraction of, what is excluded from both halves of that fraction, and, for
        `quality`, that nothing in this build measures it at all.
      */}
      <section className="wp-section" aria-labelledby="u17-help-heading" data-testid="u17-help">
        <h2 className="wp-section__title" id="u17-help-heading">
          {t('formulas.help.title')}
        </h2>
        <p className="wp-small wp-muted">{t('formulas.help.intro')}</p>
        <dl>
          {FORMULA_COMPONENT_KEYS.map((key) => (
            <div key={key}>
              <dt className="wp-small">
                {t(COMPONENT_LABEL[key])} <code>{key}</code>
              </dt>
              <dd className="wp-small wp-muted" data-testid={`u17-help-${key}`}>
                {t(COMPONENT_HELP[key])}
              </dd>
            </div>
          ))}
        </dl>
      </section>
    </div>
  );
}
